"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createBrowserStorageAdapter = createBrowserStorageAdapter;
exports.setDefaultStorageAdapter = setDefaultStorageAdapter;
exports.getOrCreateVisitorId = getOrCreateVisitorId;
exports.getOrCreateSessionId = getOrCreateSessionId;
exports.resetSessionId = resetSessionId;
exports.getUtmParamsFromUrl = getUtmParamsFromUrl;
exports.buildPageContext = buildPageContext;
exports.getOrCreateVisitorIdSync = getOrCreateVisitorIdSync;
exports.getOrCreateSessionIdSync = getOrCreateSessionIdSync;
const VISITOR_KEY = "midpoint_cv_visitor_id";
const SESSION_KEY = "midpoint_cv_session_id";
function randomId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `cv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
function createBrowserStorageAdapter() {
    if (typeof window === "undefined") {
        return null;
    }
    return {
        getItem(key) {
            try {
                if (key === SESSION_KEY && window.sessionStorage) {
                    return window.sessionStorage.getItem(key);
                }
                if (window.localStorage) {
                    return window.localStorage.getItem(key);
                }
            }
            catch {
                return null;
            }
            return null;
        },
        setItem(key, value) {
            try {
                if (key === SESSION_KEY && window.sessionStorage) {
                    window.sessionStorage.setItem(key, value);
                    return;
                }
                if (window.localStorage) {
                    window.localStorage.setItem(key, value);
                }
            }
            catch {
                // ignore
            }
        },
        removeItem(key) {
            try {
                if (key === SESSION_KEY && window.sessionStorage) {
                    window.sessionStorage.removeItem(key);
                    return;
                }
                if (window.localStorage) {
                    window.localStorage.removeItem(key);
                }
            }
            catch {
                // ignore
            }
        },
    };
}
let defaultStorage = createBrowserStorageAdapter();
function setDefaultStorageAdapter(adapter) {
    defaultStorage = adapter;
}
async function storageGet(adapter, key) {
    if (!adapter) {
        return null;
    }
    const value = adapter.getItem(key);
    return value instanceof Promise ? value : value;
}
async function storageSet(adapter, key, value) {
    if (!adapter) {
        return;
    }
    const result = adapter.setItem(key, value);
    if (result instanceof Promise) {
        await result;
    }
}
async function storageRemove(adapter, key) {
    if (!adapter) {
        return;
    }
    const result = adapter.removeItem(key);
    if (result instanceof Promise) {
        await result;
    }
}
async function getOrCreateVisitorId(options = {}) {
    if (options.existingId) {
        return options.existingId;
    }
    const adapter = options.storage ?? defaultStorage;
    const existing = await storageGet(adapter, VISITOR_KEY);
    if (existing) {
        return existing;
    }
    const created = randomId();
    await storageSet(adapter, VISITOR_KEY, created);
    return created;
}
async function getOrCreateSessionId(options = {}) {
    if (options.existingId) {
        return options.existingId;
    }
    const adapter = options.storage ?? defaultStorage;
    const existing = await storageGet(adapter, SESSION_KEY);
    if (existing) {
        return existing;
    }
    const created = randomId();
    await storageSet(adapter, SESSION_KEY, created);
    return created;
}
async function resetSessionId(options = {}) {
    const adapter = options.storage ?? defaultStorage;
    await storageRemove(adapter, SESSION_KEY);
    return getOrCreateSessionId({ storage: adapter });
}
function getUtmParamsFromUrl(url = "") {
    const utm = {};
    let searchParams;
    try {
        if (url instanceof URL) {
            searchParams = url.searchParams;
        }
        else if (typeof window !== "undefined" && !url) {
            searchParams = new URLSearchParams(window.location.search);
        }
        else {
            searchParams = new URL(url, "http://localhost").searchParams;
        }
    }
    catch {
        return utm;
    }
    ["source", "medium", "campaign", "term", "content"].forEach((key) => {
        const value = searchParams.get(`utm_${key}`);
        if (value) {
            utm[key] = value;
        }
    });
    return utm;
}
function buildPageContext(options = {}) {
    let url = options.url ?? null;
    let path = options.path ?? null;
    let title = options.title ?? null;
    let referrer = options.referrer ?? null;
    if (typeof window !== "undefined" && !url) {
        url = window.location.href;
        path = window.location.pathname;
        title = document.title || title;
        referrer = document.referrer || referrer;
    }
    const search = options.search ??
        (typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams());
    const utm = getUtmParamsFromUrl(search instanceof URLSearchParams ? `?${search.toString()}` : String(search));
    return { url, path, title, referrer, utm };
}
/** Sync helpers for browser apps that expect synchronous IDs (matches legacy runtime). */
function getOrCreateVisitorIdSync(storage = defaultStorage) {
    const existing = storage?.getItem(VISITOR_KEY);
    const resolved = existing instanceof Promise ? null : existing;
    if (resolved) {
        return resolved;
    }
    const created = randomId();
    storage?.setItem(VISITOR_KEY, created);
    return created;
}
function getOrCreateSessionIdSync(storage = defaultStorage) {
    const existing = storage?.getItem(SESSION_KEY);
    const resolved = existing instanceof Promise ? null : existing;
    if (resolved) {
        return resolved;
    }
    const created = randomId();
    storage?.setItem(SESSION_KEY, created);
    return created;
}
